import { ISourceData, ISourceDataFinding, SourceDataFindingCollectionResponse, SourceDataSingleResponse, SourceDataVerificationPermission } from '@/api/dtos';
import ConfigurationForm from '@/components/data/configuration/ConfigurationForm';
import SourceDataForm from '@/components/data/sourceData/SourceDataForm';
import Breadcrumbs from '@/components/layouts/Breadcrumbs';
import PageTitle, { TITLE_SEPARATOR } from '@/components/layouts/PageTitle';
import SkeletonExt from '@/components/layouts/SkeletonExt';
import TrialContext from '@/contexts/data/TrialContext';
import useConfigurationById from '@/hooks/data/useConfigurationById';
import useInstitution from '@/hooks/data/useInstitution';
import useMetadata from '@/hooks/data/useMetadata';
import usePatient from '@/hooks/data/usePatient';
import useSourceDataFindingsByPatientStudyNumber from '@/hooks/data/useSourceDataFindingsByPatientStudyNumber';
import useSourceDataPreviewByTrialCodeAndPatientStudyNumber from '@/hooks/data/useSourceDataPreviewByTrialCodeAndPatientStudyNumber';
import useTrialConfigurationByTrialCode from '@/hooks/data/useTrialConfigurationByTrialCode';
import usePermissions from '@/hooks/utility/usePermissions';
import { faBoxArchive, faCatSpace, faClipboardListCheck, faClipboardUser, faUser, faUsers } from '@fortawesome/pro-duotone-svg-icons';
import { LinearProgress } from '@mui/material';
import { FC, useCallback, useContext, useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';



const PatientDataRealTime: FC = () => {
    const { trialCode, configurationId, patientStudyNumber } = useParams();
    
    const { data: trial } = useContext(TrialContext);
    const { data: metadata, loading: metadataLoading, validating: metadataUpdating } = useMetadata(trialCode!, { revalidateOnFocus: false });
    const { data: patient, loading: patientLoading, validating: patientUpdating } = usePatient(patientStudyNumber!, { revalidateOnFocus: false });
    const { data: institution, loading: institutionLoading, validating: institutionUpdating  } = useInstitution(trialCode, patient?.institutionCode, { revalidateOnFocus: false });
    const { data: configuration, loading: configurationLoading, validating: configurationUpdating } = useConfigurationById(metadata && !!configurationId ? +configurationId : undefined, { revalidateOnFocus: false });
    const { data: trialConfiguration, loading: trialConfigurationLoading, validating: trialConfigurationUpdating } = useTrialConfigurationByTrialCode(!!metadata ? trialCode : undefined, { revalidateOnFocus: false });
    const { data: sourceData, loading: sourceDataLoading, validating: sourceDataUpdating, mutate: mutateSourceData } = useSourceDataPreviewByTrialCodeAndPatientStudyNumber(trialCode, patientStudyNumber, !!configurationId ? configuration : trialConfiguration, { revalidateOnFocus: false });

    const breadcrumbs = useMemo(() => {
        return [
            {
                label: 'Participants',
                link: `/${trialCode}/participants`,
                icon: faUsers
            },
            {
                label: patientStudyNumber,
                link: `/${trialCode}/participants/${patientStudyNumber}`,
                icon: faUser
            },
            {
                label: 'Data',
                link: `/${trialCode}/participants/${patientStudyNumber}/data`,
                icon: faBoxArchive
            },
            {
                label: configurationLoading ? <SkeletonExt width={120} /> : configuration?.name,
                icon: faClipboardListCheck
            }
        ];
    }, [patientStudyNumber, trialCode, configurationLoading, configuration]);

    const updateSourceData = useCallback(async (newData?: ISourceData) => {
        if (newData) {
            await mutateSourceData(new SourceDataSingleResponse({
                data: newData
            }), true);
        }
    }, [mutateSourceData, sourceData]);

    const loadingData = metadataLoading || (!!configurationId && configurationLoading) || (patient?.institutionCode && institutionLoading) || patientLoading || sourceDataLoading || trialConfigurationLoading;
    const updating = !loadingData && (metadataUpdating || (!!configurationId && configurationUpdating) || (patient?.institutionCode && institutionUpdating) || patientUpdating || sourceDataUpdating || trialConfigurationUpdating);
    
    const { 
        data: [
            hasViewSourceData
        ] = [], 
        loading: permissionsLoading, 
        validating: permissionsValidating
    } = usePermissions(
        [
            SourceDataVerificationPermission.ViewSourceData
        ], 
        loadingData,
        trial?.id,
        undefined,
        undefined,
        undefined,
        patient?.institutionId,
        patient?.id,
        undefined,
        undefined,
        undefined,
        {
            revalidateOnFocus: false
        }
    );
    
    if (!hasViewSourceData && !permissionsLoading && !permissionsValidating) {
        return (
            <Navigate to='/error/403' />
        )
    }
    
    
    const loading = loadingData || permissionsLoading || permissionsValidating;

    return (
        <>
            <PageTitle title={`${configuration?.name}${TITLE_SEPARATOR}${patientStudyNumber}${TITLE_SEPARATOR}${trial?.name}`} />
            <Breadcrumbs 
                breadcrumbs={breadcrumbs}
            />
            {
                loading && (
                    <LinearProgress />
                )
            }
            {
                !loading && (
                    <SourceDataForm
                        hideVerification
                        hasBreadcrumbs
                        readOnly
                        configuration={!!configurationId ? configuration : trialConfiguration}
                        trialConfiguration={trialConfiguration}
                        trial={trial}
                        institution={institution}
                        patient={patient}
                        metadata={metadata}
                        data={sourceData}
                        loading={loading}
                        updating={updating}
                        updateSourceData={updateSourceData}
                    />
                )
            }
        </>
    );
}

export default PatientDataRealTime;